import { BRAND } from "@gridaction/core";
import { cn } from "@/lib/utils";

/**
 * App icon: a tile with a small grid and one "winning" square. Colours are
 * literals because an SVG here can't read CSS variables that change with the
 * theme — keep them in step with `tokens.css` when rebranding.
 */
export function BrandMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={cn("size-8", className)} aria-hidden>
      <rect width="32" height="32" rx="8" fill="#0f1d3a" />
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={6 + col * 7}
            y={6 + row * 7}
            width="6"
            height="6"
            rx="1.5"
            fill={
              row === 1 && col === 2 ? "#f2b632" : row === 2 && col === 0 ? "#4cc35a" : "#2a3b66"
            }
          />
        )),
      )}
    </svg>
  );
}

/** A little grid that stands in for the "I" in GRID. */
function GridLetter() {
  return (
    <svg
      viewBox="0 0 10 22"
      className="inline-block h-[0.74em] w-[0.34em] self-center"
      aria-hidden
      fill="currentColor"
    >
      {[0, 1, 2, 3].map((row) =>
        [0, 1].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={col * 5.5}
            y={row * 5.75}
            width="4.5"
            height="4.75"
            rx="1"
            className={row === 1 && col === 1 ? "text-gold-strong" : "text-primary"}
            fill="currentColor"
          />
        )),
      )}
    </svg>
  );
}

/**
 * The GridAction wordmark: GRID in the ink color with a grid for its I, and
 * ACTION in green. `stacked` puts ACTION under GRID, for big hero use.
 */
export function Wordmark({ className, stacked }: { className?: string; stacked?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex font-black tracking-tight uppercase leading-none",
        stacked ? "flex-col items-center gap-[0.04em]" : "items-baseline gap-[0.28em]",
        className,
      )}
    >
      <span className="sr-only">{BRAND.name}</span>
      <span className="inline-flex items-baseline gap-[0.04em] text-foreground" aria-hidden>
        {BRAND.wordmark.before}
        <GridLetter />
        {BRAND.wordmark.after}
      </span>
      <span className="text-primary" aria-hidden>
        {BRAND.wordmark.second}
      </span>
    </span>
  );
}

export function Brand({
  className,
  compact,
  shortOnPhones,
}: {
  className?: string;
  /** The icon alone. */
  compact?: boolean;
  /**
   * The icon alone on a narrow phone, the name beside it from 420px up. For a top bar that also holds
   * buttons: with the name too it needs 417px in the widest font tried (about 382px in a font with Arial's
   * letter widths), so on a narrower phone the page grew wider than the screen.
   */
  shortOnPhones?: boolean;
}) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <BrandMark />
      {compact ? null : (
        <Wordmark className={cn("text-lg", shortOnPhones && "hidden min-[420px]:inline-flex")} />
      )}
    </span>
  );
}
